import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

function getStoredTokens() {
  try {
    return {
      access_token: localStorage.getItem('access_token'),
      refresh_token: localStorage.getItem('refresh_token'),
    };
  } catch (e) {
    console.warn('Failed to read tokens from storage:', e);
    return { access_token: null, refresh_token: null };
  }
}

function storeTokens(access_token, refresh_token) {
  try {
    if (access_token) localStorage.setItem('access_token', access_token);
    if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
  } catch (e) {
    console.warn('Failed to persist tokens:', e);
  }
}

function clearTokens() {
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  } catch (e) {
    console.warn('Failed to clear tokens:', e);
  }
}

function authHeaders() {
  const { access_token } = getStoredTokens();
  return access_token ? { Authorization: `Bearer ${access_token}` } : {};
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const { access_token } = getStoredTokens();
    if (!access_token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      setUser(response.data);
    } catch (e) {
      console.warn('Auth check failed — clearing tokens:', e?.message || e);
      clearTokens();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const data = response.data;
    if (data?.mfa_required) {
      return data;
    }
    if (data.access_token) storeTokens(data.access_token, data.refresh_token);
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, { email, password, name });
    const data = response.data;
    if (data.access_token) storeTokens(data.access_token, data.refresh_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { headers: authHeaders() });
    } catch (e) {
      console.warn('Server logout failed (clearing local tokens anyway):', e?.message || e);
    }
    clearTokens();
    setUser(null);
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    const response = await axios.post(`${API_URL}/api/auth/google/session`, { session_id: sessionId });
    const data = response.data;
    if (data.access_token) storeTokens(data.access_token, data.refresh_token);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout,
      googleLogin, processGoogleSession, checkAuth, setUser,
      storeTokens, authHeaders
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { authHeaders, getStoredTokens, storeTokens, clearTokens };
